/**
 * usePolicyVersions — the Policies-page "See history" control (dms/spec.md
 * §4.3, §M12): "'See history' per policy → reveals older versions
 * (version_group_id group; open/download each)."
 *
 * The grid's list wire (`PolicyListItemDto`) carries no document id, so this
 * reuses the EXISTING `GET /policies/:id/versions` endpoint (Story 9.1 — the
 * VVG policy_version chain, already live-wired via `usePoliceDetail`) rather
 * than inventing a new wire field: each `PolicyVersionDto.source_document_id`
 * IS the PDF that produced that version (dms/spec.md §0 reconciliation table),
 * so the version list already carries exactly what "open/download each" needs
 * — no DB/API change required for a web-only module.
 *
 * Lazy: only fetches once the caller flips `enabled` (the row's "Verlauf
 * anzeigen" click), not for every row up front.
 *
 * live mode → GET /policies/:id/versions.
 * mock mode → the deterministic POLICY_VERSIONS_MOCK fixture.
 *
 * [Source: apps/web/src/features/entity-detail/hooks/useEntityDetail.ts#usePoliceDetail] (versions precedent)
 * [Source: projects/broker-ag/dms/spec.md §4.3, §M12]
 */
import { useQuery } from '@tanstack/react-query';
import type { PolicyVersionDto } from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { policyVersionsFor } from '../mock';

export function usePolicyVersions(policyId: string, enabled: boolean) {
  const live = isLiveData();

  const query = useQuery<PolicyVersionDto[]>({
    queryKey: qk.policyVersions(policyId),
    queryFn: () =>
      live ? apiClient.policies.versions(policyId) : Promise.resolve(policyVersionsFor(policyId)),
    enabled,
    ...(live ? {} : { staleTime: Infinity }),
  });

  const versions = query.data ?? [];
  // Newest first for display ("reveal the OLDER versions" reads top-down);
  // the api returns version_nr ASC (oldest first).
  const olderFirst = [...versions].sort((a, b) => b.version_nr - a.version_nr);

  return {
    versions: olderFirst,
    isLoading: enabled && query.isLoading,
    isError: enabled && query.isError,
  };
}
